/**
 * guidedMapStore — 가이드 맵 프로젝트 생성 위저드의 진행 상태.
 *
 * 셋업 페이지(/map-editor/guided 에서 이름/층 입력 → 생성)와 에디터
 * (DXF → 레이어 매핑 → 조명 그룹핑 → PDF 매핑 → 관계 생성 → 저장)
 * 두 페이지에 걸쳐 진행되므로 페이지 로컬 state 가 아니라 스토어로
 * 유지한다(같은 SPA 내 네비게이션 생존, 새로고침 시에는 종료 — 의도된 동작).
 *
 * 단계 완료 신호는 두 종류다:
 *  · 관찰형 — EditorGuide 가 기존 상태(매핑 모달 열림, elementCount,
 *    mappingStep 등)를 지켜보고 스스로 advance.
 *  · 통지형 — 모달 confirm 핸들러(조명 그룹핑·관계 생성)가 completeStep 을
 *    호출. 가이드가 꺼져 있거나 현재 단계가 아니면 no-op 이라 어디서든
 *    안전하게 부를 수 있다.
 */
import { create } from 'zustand'

export type GuidePhase = 'idle' | 'editor'

export type EditorGuideStep =
  | 'dxf'
  | 'layerMap'
  | 'lights'
  | 'pdf'
  | 'relations'
  | 'save'
  | 'done'

export const EDITOR_GUIDE_STEP_ORDER: EditorGuideStep[] = [
  'dxf',
  'layerMap',
  'lights',
  'pdf',
  'relations',
  'save',
  'done',
]

/** 셋업 페이지(GuidedSetupPage)의 4단계 — 그 페이지 자체 내비게이션에 사용. */
export type SetupStep = 'name' | 'floor' | 'ip' | 'confirm'
export const SETUP_STEP_ORDER: SetupStep[] = ['name', 'floor', 'ip', 'confirm']

/**
 * 셋업 페이지 4단계 + 에디터 가이드 7단계를 하나로 이은 전체 진행 순서.
 * 두 페이지(셋업/에디터)가 서로 다른 컴포넌트·라우트라도 GuideProgress 가
 * 항상 "N / 11 단계" 로 동일하게 이어지는 진행 표시를 그릴 수 있게 한다.
 */
export type FullGuideStep = SetupStep | EditorGuideStep
export const FULL_GUIDE_STEP_ORDER: FullGuideStep[] = [...SETUP_STEP_ORDER, ...EDITOR_GUIDE_STEP_ORDER]

export const FULL_GUIDE_STEP_LABELS: Record<FullGuideStep, string> = {
  name: '이름',
  floor: '층',
  ip: 'CCTV IP',
  confirm: '확인',
  dxf: 'DXF 업로드',
  layerMap: '레이어 매핑',
  lights: '조명 그룹핑',
  pdf: 'PDF 매핑',
  relations: '관계 생성',
  save: '저장',
  done: '완료',
}

interface GuidedMapState {
  phase: GuidePhase
  step: EditorGuideStep
  /** 셋업 페이지에서 입력받은 프로젝트 이름(에디터 가이드 인사에 사용). */
  projectName: string | null

  /** 프로젝트 생성 완료 → 에디터 가이드 시작(네비게이션 직전 호출). */
  enterEditor: (projectName: string) => void
  /** 특정 단계로 이동(관찰형 전이·건너뛰기). */
  advance: (to: EditorGuideStep) => void
  /** 통지형 완료 — 에디터 단계이고 현재 단계와 일치할 때만 다음으로. */
  completeStep: (done: EditorGuideStep) => void
  /** 가이드 종료. */
  end: () => void
}

export const useGuidedMapStore = create<GuidedMapState>((set, get) => ({
  phase: 'idle',
  step: 'dxf',
  projectName: null,

  enterEditor: (projectName: string) =>
    set({ phase: 'editor', step: 'dxf', projectName }),

  advance: (to: EditorGuideStep) => {
    if (get().phase !== 'editor') return
    set({ step: to })
  },

  completeStep: (done: EditorGuideStep) => {
    const { phase, step } = get()
    if (phase !== 'editor' || step !== done) return
    const idx = EDITOR_GUIDE_STEP_ORDER.indexOf(done)
    const next = EDITOR_GUIDE_STEP_ORDER[idx + 1]
    if (next) set({ step: next })
  },

  end: () => set({ phase: 'idle', step: 'dxf', projectName: null }),
}))
